import {useCallback, useEffect, useRef} from 'react';

import iccSsoService from '@/services/icc/iccSsoService';
import {scheduleSsoCookieRefresh} from '@/services/sso/ssoCookieRefreshScheduler';
import {selectSsoLinks} from '@/state/selectors/media';
import {getState} from '@/state/store';

type UseSsoCookieRefreshParams = {
    disabled: boolean;
};

/**
 * Wires ssoCookieRefreshScheduler into the component lifecycle.
 * On timer fire, triggers a batch re-fetch with includeSessionInfo=true
 * using the first cached SSO link from Redux as payload.
 */
const useSsoCookieRefresh = ({disabled}: UseSsoCookieRefreshParams): void => {
    const disposeRef = useRef<(() => void) | null>(null);

    const schedule = useCallback(() => {
        disposeRef.current?.();

        disposeRef.current = scheduleSsoCookieRefresh({
            onRefresh: async () => {
                // Find the first cached SSO link from Redux to use as payload
                const state = getState!();
                const {byTemplate = {}, byUrl = {}} = selectSsoLinks(state) || {};
                const firstTemplateKey = Object.keys(byTemplate)[0];
                const firstUrlKey = Object.keys(byUrl)[0];

                if (firstUrlKey) {
                    await iccSsoService.getSsoLinks({
                        ssoTemplates: [],
                        urls: [firstUrlKey],
                        includeSessionInfo: true,
                    });
                } else if (firstTemplateKey) {
                    await iccSsoService.getSsoLinks({
                        ssoTemplates: [firstTemplateKey],
                        urls: [],
                        includeSessionInfo: true,
                    });
                }

                // Re-schedule after successful refresh
                schedule();
            },
        });
    }, []);

    useEffect(() => {
        if (disabled) return;

        schedule();

        return () => {
            disposeRef.current?.();
            disposeRef.current = null;
        };
    }, [disabled, schedule]);
};

export default useSsoCookieRefresh;
